// angular
//     .module("HeroPRO")
//     .factory("FRDocumentsFactory", fundingRequestDocumentsFactory);

import _ from 'lodash';

/* @ngInject */
export default function fundingRequestDocumentsFactory(HPStateMachineConfig, $q, FRInformationFactory, FRClosingFactory, HPConditionTypes) {
    let _fundingRequestDocumentsPromise;
    let _completionCertificateRegenerated = false;
    let _appInfo = {};
    let _stateMachineData = {};
    let _fundingRequest = {};
    let _eSignDocumentsRaw = {}; // _eSignDocumentsRaw refers to the data objrvy returned from the server accessible via FRClosingFactory.getESignDocuments()
    let _invalidDocuments = [];
    let _otherDocuments = [];
    let _financialDocuments = [];
    let _eSignDocuments = []; // _eSignDocuments refers to the array of documents that need to be eSigned. This is dynamically contstructed from within this factory
    let _outstandingDocuments = [];
    let _CCDocument = null;
    let _ACADocument = null;
    let _signaturesButtonVisible = false;

    return {
        getFundingRequestDocuments,
        getFundingRequestDocumentsPromise,
        setCompletionCertificateRegenerated,
        getCompletionCertificateRegenerated,
        getInvalidDocuments,
        getOtherDocuments,
        getFinancialDocuments,
        getESignDocuments,
        getCCDocument,
        getACADocument,
        getSignaturesButtonVisibility,
        getOutstandingDocuments,
    };

    function getFundingRequestDocuments() {
        resetFundingRequestDocuments();
        _fundingRequestDocumentsPromise = $q((resolve) => {
            // Before processing the documents, ensure that all async data that is needed is available
            $q.all([FRInformationFactory.getFundingRequestInformationPromise(), FRClosingFactory.getFundingRequestClosingPromise()]).then(() => {
                _appInfo = FRInformationFactory.getAppInfo();
                _stateMachineData = FRInformationFactory.getStateMachineData();
                _fundingRequest = FRClosingFactory.getFundingRequest();
                _eSignDocumentsRaw = FRClosingFactory.getESignDocuments();

                processACA();
                processCompletionCertificate();
                processOtherDocuments();
                processFinancialDocuments();
                setSignaturesButtonVisibility();
                processOutstandingDocuments();

                // Resolve the _fundingRequestDocumentsPromise now that all the documents have been processed
                resolve();
            });
        });

        return _fundingRequestDocumentsPromise;
    }

    function getFundingRequestDocumentsPromise() {
        return _fundingRequestDocumentsPromise;
    }

    function resetFundingRequestDocuments() {
        _completionCertificateRegenerated = false;
        _appInfo = {};
        _stateMachineData = {};
        _fundingRequest = {};
        _eSignDocumentsRaw = {};
        _invalidDocuments = [];
        _otherDocuments = [];
        _financialDocuments = [];
        _eSignDocuments = [];
        _outstandingDocuments = [];
        _CCDocument = null;
        _ACADocument = null;
        _signaturesButtonVisible = false;
    }

    function getInvalidDocuments() {
        return _invalidDocuments;
    }

    function getOtherDocuments() {
        return _otherDocuments;
    }

    function getFinancialDocuments() {
        return _financialDocuments;
    }

    function getESignDocuments() {
        return _eSignDocuments;
    }

    function getCCDocument() {
        return _CCDocument;
    }

    function getACADocument() {
        return _ACADocument;
    }

    function processOtherDocuments() {
        _.each(_fundingRequest.ConditionDetails, (condition) => {
            setLatestAttachment(condition);
            if (condition.ConditionTypeDetail.ConditionTypeId !== HPConditionTypes.CompletionCertificate) {
                if (conditionHasRejections(condition) === true &&
                    _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Closer_Rejected_Documents) {
                    _invalidDocuments.push(condition);
                } else {
                    _otherDocuments.push(condition);
                }
            }
        });

        // Flag the invoice/s
        _.each(_otherDocuments, (document) => {
            document.Invoice = !!((document.ConditionTypeDetail.ConditionTypeId === HPConditionTypes.Invoice ||
                                document.ConditionTypeDetail.ConditionTypeId === HPConditionTypes.HIC));
        });
    }

    function processFinancialDocuments() {
        if (!!FRInformationFactory.getFinancialDocuments().Documents) {
            _financialDocuments = FRInformationFactory.getFinancialDocuments().Documents;
        }
    }

    function processACA() {
        if (!!_appInfo.AssessmentContractAddendums && _appInfo.AssessmentContractAddendums.length > 0) {
            const pendingOrCompletedACAs = _.filter(_appInfo.AssessmentContractAddendums, document =>
                // Take only Pending or Completed ACA's
                document.AssessmentContractAddendumStatusId === 1 || document.AssessmentContractAddendumStatusId === 2);
            if (pendingOrCompletedACAs.length !== 0) {
                _ACADocument = _.max(pendingOrCompletedACAs, document =>
                    // Take the most recent ACA
                    Date.parse(document.CreatedDate));

            	_eSignDocuments[_eSignDocuments.length] = _ACADocument;
                _ACADocument.Description = 'Change Order';
                _ACADocument.ESignDocumentType = 'ACA';

                // Grab esign document envelope information if available
                if (!_eSignDocumentsRaw.AssesmentContractAddendum) {
                    _ACADocument.ESignDocumentEnvelope = null;
                } else {
                    _ACADocument.ESignDocumentEnvelope = _eSignDocumentsRaw.AssesmentContractAddendum;
                    _ACADocument.ESignDocumentEnvelope.EDocumentRecipients = _.filter(_ACADocument.ESignDocumentEnvelope.EDocumentRecipients, recipient => !recipient.EDocumentRole);
                    setACADocumentStatus();
                }
            }
        }
    }

    function setACADocumentStatus() {
        _ACADocument.status = {};
        _ACADocument.status.numberOfPropertyOwners = _ACADocument.ESignDocumentEnvelope.EDocumentRecipients.length;
        _ACADocument.status.propertyOwnersSignatureStatus = _.defaults(_.countBy(_ACADocument.ESignDocumentEnvelope.EDocumentRecipients, recipient => (recipient.EDocumentRecipientStatus === 5 ? 'signed' : 'unsigned')), { signed: 0, unsigned: 0 });
        _ACADocument.status.eSigned = _ACADocument.status.numberOfPropertyOwners === _ACADocument.status.propertyOwnersSignatureStatus.signed;
        // ACA can be uploaded from OpsWeb, but not from HEROPro
        _ACADocument.status.ACAUploaded = _ACADocument.status.propertyOwnersSignatureStatus.signed === 0 && _stateMachineData.ACAStateId === HPStateMachineConfig.ACA.COG_Countersigned_ACA;
    }

    function processCompletionCertificate() {
        _CCDocument = _.find(_fundingRequest.ConditionDetails, condition => (condition.ConditionTypeDetail.ConditionTypeId === HPConditionTypes.CompletionCertificate));

        if (!!_CCDocument) {
            setLatestAttachment(_CCDocument);
            _CCDocument.ESignDocumentType = 'CC';

            if (conditionHasRejections(_CCDocument) === true &&
                _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Closer_Rejected_Documents) {
                _invalidDocuments.push(_CCDocument);
            } else {
                _eSignDocuments.push(_CCDocument);
            }
            // Grab esign document envelope information if available
            if (!_eSignDocumentsRaw.CompletionCertificate) {
                _CCDocument.ESignDocumentEnvelope = null;
            } else {
                _CCDocument.ESignDocumentEnvelope = _eSignDocumentsRaw.CompletionCertificate;
                _CCDocument.ESignDocumentEnvelope.EDocumentRecipients = _.filter(_CCDocument.ESignDocumentEnvelope.EDocumentRecipients, recipient => !recipient.EDocumentRole);
            }

            setCCDocumentStatus();
        } else {
            _CCDocument = null;
        }
    }

    function setCCDocumentStatus() {
        _CCDocument.status = {};
        _CCDocument.status.CCUploaded = false;

        if (_CCDocument.ESignDocumentEnvelope !== null) {
            _CCDocument.status.numberOfPropertyOwners = _CCDocument.ESignDocumentEnvelope.EDocumentRecipients.length;
            _CCDocument.status.propertyOwnersSignatureStatus = _.defaults(_.countBy(_CCDocument.ESignDocumentEnvelope.EDocumentRecipients, recipient => (recipient.EDocumentRecipientStatus === 5 ? 'signed' : 'unsigned')), { signed: 0, unsigned: 0 });
        }

        _CCDocument.status.eSigned = _stateMachineData.CompletionCertificateStateId === HPStateMachineConfig.CC.Completion_Cert_Uploaded_Or_eSigned;
        
        if (!!_CCDocument.AttachmentDetails && _CCDocument.AttachmentDetails.length > 0) {
            _CCDocument.status.CCUploaded = (_.max(_CCDocument.AttachmentDetails, document =>
                // Retrieve the most recent attachment
                // Keep the === undefined here. ¯\_(ツ)_/¯
                Date.parse(document.CreatedDate)).AttachmentDetail.ESigned === undefined);
        }
    }

    function setLatestAttachment(condition) {
        if (!!condition.AttachmentDetails && condition.AttachmentDetails.length > 0) {
            condition.LatestAttachment = _.max(condition.AttachmentDetails, attachment => Date.parse(attachment.CreatedDate));
        } else {
            condition.LatestAttachment = undefined;
        }
    }

    function setSignaturesButtonVisibility() {
        const ACANeedsSignatures = _ACADocument !== null && _ACADocument.ESignDocumentEnvelope !== null && !_ACADocument.status.eSigned && !_ACADocument.status.ACAUploaded;

        const CCNeedsSignatures = _stateMachineData.CompletionCertificateStateId === HPStateMachineConfig.CC.CO_Signed_Completion_Cert ||
                                _stateMachineData.CompletionCertificateStateId === HPStateMachineConfig.CC.CO_Emailed_PO_Completion_Cert ||
                                _stateMachineData.CompletionCertificateStateId === HPStateMachineConfig.CC.Completion_Cert_Sent_To_Installer ||
                                _stateMachineData.CompletionCertificateStateId === HPStateMachineConfig.CC.PO_Initiated_Completion_Cert_eSign ||
                                _stateMachineData.CompletionCertificateStateId === HPStateMachineConfig.CC.PO_Completed_Completion_Cert_eSign;

        _signaturesButtonVisible = ACANeedsSignatures || CCNeedsSignatures;

    }

    function getSignaturesButtonVisibility() {
        return _signaturesButtonVisible;
    }

    function processOutstandingDocuments() {
        _.each(_otherDocuments, (document) => {
            if (!document.AttachmentDetails || document.AttachmentDetails.length == 0) {
                _outstandingDocuments[_outstandingDocuments.length] = document;
            }
        });
    }

    function getOutstandingDocuments() {
        return _outstandingDocuments;
    }

    function conditionHasRejections(condition) {
        return !!condition.LatestAttachment && !!condition.LatestAttachment.RejectionDetails && condition.LatestAttachment.RejectionDetails.length > 0;
    }

    function setCompletionCertificateRegenerated(newValue) {
        _completionCertificateRegenerated = newValue;
    }

    function getCompletionCertificateRegenerated() {
        return _completionCertificateRegenerated;
    }
}



// WEBPACK FOOTER //
// ./src/app/appDetail/fundingRequestDocumentsFactory.js